
import { useState, useRef } from 'react';

export default function Home() {
  const [isSpinning, setIsSpinning] = useState(false);
  const [result, setResult] = useState<'yes' | 'no' | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [lastResultTime, setLastResultTime] = useState<number | null>(null);
  const [lockedResult, setLockedResult] = useState<'yes' | 'no' | null>(null);
  const [yesImage, setYesImage] = useState(
    'https://static.readdy.ai/image/c2784296c91e4fdfa438138a6e6ce7cc/402272145bb42e284dcf8e5ab3a938be.png'
  );
  const [noImage, setNoImage] = useState(
    'https://static.readdy.ai/image/c2784296c91e4fdfa438138a6e6ce7cc/2e890e69e3799441b4394e594f62642c.png'
  );

  const audioContextRef = useRef<AudioContext | null>(null);
  const spinSoundIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const getAudioContext = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      
      // Resume context if suspended
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      
      return audioContextRef.current;
    } catch (e) {
      console.error('AudioContext creation failed:', e);
      return null;
    }
  };

  const playSpinSound = () => {
    try {
      const audioContext = getAudioContext();
      if (!audioContext || audioContext.state !== 'running') return;

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + 0.08);

      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.08);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.08);
    } catch (e) {
      console.error('Spin sound failed:', e);
    }
  };

  const startSpinSoundLoop = () => {
    // Clear any existing interval first
    if (spinSoundIntervalRef.current) {
      clearInterval(spinSoundIntervalRef.current);
      spinSoundIntervalRef.current = null;
    }
    
    // Initialize audio context on user interaction
    const audioContext = getAudioContext();
    if (!audioContext) return;
    
    // Play initial sound
    playSpinSound();
    
    // Set up interval to play sound every 120ms during spinning
    spinSoundIntervalRef.current = setInterval(() => {
      playSpinSound();
    }, 120);
  };

  const stopSpinSoundLoop = () => {
    if (spinSoundIntervalRef.current) {
      clearInterval(spinSoundIntervalRef.current);
      spinSoundIntervalRef.current = null;
    }
  };

  const playResultSound = (isYes: boolean) => {
    try {
      const audioContext = getAudioContext();
      if (!audioContext || audioContext.state !== 'running') return;

      // Create a simple, clear "ding" sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      if (isYes) {
        // Higher pitched "ding" for YES
        oscillator.frequency.setValueAtTime(1200, audioContext.currentTime);
      } else {
        // Slightly lower pitched "ding" for NO
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      }

      // Bell-like envelope - quick attack, sustained ring, gradual decay
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 1.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 1.5);
    } catch (e) {
      console.error('Result sound failed:', e);
    }
  };

  const startGame = () => {
    if (isSpinning) return;

    setIsSpinning(true);
    setShowResult(false);
    
    const currentTime = Date.now();
    let finalResult: 'yes' | 'no';

    // Check if we should use locked result (within 15 seconds of last result)
    if (lastResultTime && lockedResult && (currentTime - lastResultTime) < 15000) {
      finalResult = lockedResult;
    } else {
      // Generate new random result
      finalResult = Math.random() > 0.5 ? 'yes' : 'no';
      setLockedResult(finalResult);
    }

    setResult(finalResult);

    // Start continuous spin sound
    startSpinSoundLoop();

    setTimeout(() => {
      setIsSpinning(false);
      stopSpinSoundLoop(); // Stop the spinning sound
      setLastResultTime(Date.now()); // Record the time of this result
      
      // Small delay before result sound to avoid overlap
      setTimeout(() => {
        playResultSound(finalResult === 'yes');
      }, 100);

      // Show result popup after a tiny pause
      setTimeout(() => {
        setShowResult(true);
      }, 500);
    }, 3000);
  };

  const closeResult = () => {
    setShowResult(false);
  };

  const handleImageUpload = (type: 'yes' | 'no', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      if (type === 'yes') {
        setYesImage(imageUrl);
      } else {
        setNoImage(imageUrl);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-4 relative"
      style={{
        background: `
          linear-gradient(to bottom right, rgba(147, 51, 234, 0.8), rgba(236, 72, 153, 0.8), rgba(239, 68, 68, 0.8)),
          url('https://static.readdy.ai/image/c2784296c91e4fdfa438138a6e6ce7cc/3ea4b5b2884a861cd180a880cb342f20.jfif')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* 添加一個半透明遮罩層來確保文字可讀性 */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-600/40 via-pink-500/40 to-red-500/40"></div>
      
      {/* 所有內容需要相對定位以顯示在背景之上 */}
      <div className="relative z-10 flex flex-col items-center justify-center w-full">
        {/* Logo */}
        <div className="mb-6">
          <img 
            src="https://static.readdy.ai/image/c2784296c91e4fdfa438138a6e6ce7cc/e62c0bb389d86f3aec7793c76e66ba4b.png" 
            alt="Noir Blanc Logo" 
            className="w-20 h-20 object-contain"
          />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1 leading-tight" style={{ fontFamily: 'Anton, Impact, Arial Black, sans-serif' }}>
            Noir Blanc 惡魔與天使
          </h1>
          <div className="flex items-center justify-center space-x-2 mb-2">
            <span className="text-white/70 text-sm">之</span>
            <span className="text-white text-xl sm:text-2xl font-semibold">命運交響曲</span>
          </div>
          <p className="text-white/80 text-sm px-4">當你猶豫不決時，讓惡魔與天使為你決定！</p>
        </div>

        {/* Coin display */}
        <div className="relative mb-8" style={{ perspective: '1000px' }}>
          <div
            className={`w-48 h-48 rounded-full border-8 border-white/20 shadow-2xl relative transition-transform duration-300`}
            style={{
              transformStyle: 'preserve-3d',
              animation: isSpinning ? 'flipY 0.1s linear infinite' : 'none',
              transform: result === 'no' ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
          >
            {/* Front side – YES */}
            <div
              className="absolute inset-0 w-full h-full rounded-full bg-white flex items-center justify-center"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <img src={yesImage} alt="Yes" className="w-32 h-32 object-contain" />
            </div>

            {/* Back side – NO */}
            <div
              className="absolute inset-0 w-full h-full rounded-full bg-white flex items-center justify-center"
              style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
            >
              <img src={noImage} alt="No" className="w-32 h-32 object-contain" />
            </div>
          </div>
        </div>

        {/* Start button */}
        <button
          onClick={startGame}
          disabled={isSpinning}
          className={`px-8 py-4 rounded-full text-white font-bold text-xl shadow-lg transform transition-all duration-200 ${
            isSpinning
              ? 'bg-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 hover:scale-105 active:scale-95'
          } ${!isSpinning ? 'animate-pulse' : ''}`}
        >
          <div className="flex items-center space-x-2">
            <i className="ri-play-fill"></i>
            <span>{isSpinning ? '翻轉中...' : '開始遊戲'}</span>
          </div>
        </button>

        {/* Image upload section */}
        <div className="mt-8 flex space-x-4">
          {/* YES image upload */}
          <div className="text-center">
            <label className="block text-white mb-2 font-medium">更換 YES 圖片</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload('yes', e)}
              className="hidden"
              id="yes-upload"
            />
            <label
              htmlFor="yes-upload"
              className="cursor-pointer bg-green-500 hover:bg-green-600
              text-white px-4 py-2 rounded-lg transition-colors duration-200 inline-flex items-center space-x-2"
            >
              <i className="ri-upload-2-line"></i>
              <span>上傳</span>
            </label>
          </div>

          {/* NO image upload */}
          <div className="text-center">
            <label className="block text-white mb-2 font-medium">更換 NO 圖片</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload('no', e)}
              className="hidden"
              id="no-upload"
            />
            <label
              htmlFor="no-upload"
              className="cursor-pointer bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 inline-flex items-center space-x-2"
            >
              <i className="ri-upload-2-line"></i>
              <span>上傳</span>
            </label>
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-8 text-center text-white/70 max-w-md">
          <p className="text-sm">
            <i className="ri-lightbulb-line mr-1"></i>
            提示：當理性退場，直覺登場。拋起硬幣的瞬間，你心底已有答案。
          </p>
          {lastResultTime && lockedResult && (
            <p className="text-xs mt-2 text-white/50">
              www.noirblanc.me
            </p>
          )}
        </div>
      </div>

      {/* Result modal */}
      {showResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center transform animate-smooth-entrance">
            <div className="mb-6">
              <div
                className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 ${
                  result === 'yes' ? 'bg-green-100' : 'bg-red-100'
                }`}
              >
                <img
                  src={result === 'yes' ? yesImage : noImage}
                  alt={result === 'yes' ? 'Yes' : 'No'}
                  className="w-16 h-16 object-contain"
                />
              </div>
              <h2
                className={`text-3xl font-bold mb-4 ${
                  result === 'yes' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {result === 'yes' ? 'YES!' : 'NO!'}
              </h2>
              <p className="text-gray-700 text-sm leading-relaxed">
                {result === 'yes' 
                  ? 'Noir 惡魔說：去做吧！用心去做，享受經努力得出的任何結果！唔信命就再轉多次吧！' 
                  : 'Blanc 天使說：還是算了吧！如果您要靠命運告訴您，要不要向前的話！還是想再轉多次？'}
              </p>
            </div>

            <button
              onClick={closeResult}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-full transition-colors duration-200"
            >
              關閉
            </button>
          </div>
        </div>
      )}

      {/* Local CSS */}
      <style jsx>{`
        @keyframes flipY {
          0% {
            transform: rotateY(0deg);
          }
          100% {
            transform: rotateY(360deg);
          }
        }

        @keyframes smooth-entrance {
          0% {
            opacity: 0;
            transform: scale(0.8) translateY(-20px);
          }
          50% {
            opacity: 1;
            transform: scale(1.05) translateY(-10px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-smooth-entrance {
          animation: smooth-entrance 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
}
